// Sub-tab B — "הוספת נכסים": the pre-filtered discovery surface (mock s18 +
// Haim's review round). The three sources — שלי / משרד / מהשוק — are a TAB STRIP
// (AddListingsSourceTabs): one source at a time, every count on its tab, and the
// first source that has results auto-selected. They used to render stacked in one
// scroll, where the results the realtor wanted were routinely buried under the
// other two sections. Everything else is unchanged: editable filter chips seeded
// from the contact's search profile, a free-text search bar feeding the server's
// query, transparent match score, one-tap add/remove toggle, a listing quick-view,
// and the subtle profile-update suggestion when filters diverge.
import { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Button,
  Skeleton,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress
} from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { Check, Search, Replay } from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import Label from "components/Label";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import { getUserFirstNameFallBackToPhoneNumber } from "utils/Haim/utils";
import {
  pickListingDisplayCoverUrl,
} from "utils/listings/watermarkFree";
import { useContactSurfaceDown } from "../../contactSurface";
import type {
  RealEstateContact,
  FavoriteListingsOfContact
} from "../../../../../../@types/sharedSchema";
import type { MatchHit, MatchSegmentId } from "./matchesSearch";
import type { MatchFilters } from "./contactMatchFilters";
import type { ScoredMatchHit, MatchesSearch } from "./useMatchesSearch";
import { addListingToContactMinisite, removeListingFromContactMinisite } from "./matchesData";
import MatchFilterChips from "./MatchFilterChips";
import MatchesFiltersDrawer from "./MatchesFiltersDrawer";
import AddListingsSourceTabs, {
  getAddListingsSource,
  useAddListingsSourceTab
} from "./AddListingsSourceTabs";
import type { AddListingsSourceCount } from "./AddListingsSourceTabs";
import ProfileUpdateSuggestion from "./ProfileUpdateSuggestion";
import ListingQuickViewDialog from "./ListingQuickViewDialog";
import ListingPriceWithHistory from "./ListingPriceWithHistory";
import { ListingOrgActivityIndicator } from "components/_dashboard/listings/orgActivity/ListingOrgActivityIndicator";
import SellerImageOverlay from "./SellerImageOverlay";
import { buildListingMetaLine, buildListingAddressLine } from "./matchesDisplay";

const ORIGIN_TAGS: Record<string, string> = { yad2: "יד2", madlan: "מדלן" };

// Non-paid users see the first FREE_MARKET_RESULTS market cards; the rest are blurred
// (port of the legacy listingsMatch paid-tier blur — applied to the scraped-market
// segment, which is the paid value; the realtor's own/office listings are never gated).
const FREE_MARKET_RESULTS = 5;

type AddListingsSubTabProps = {
  contact: RealEstateContact;
  favorites: FavoriteListingsOfContact | null | undefined;
  search: MatchesSearch;
  filters: MatchFilters;
  onFiltersChange: (patch: Partial<MatchFilters>) => void;
  filtersEdited: boolean;
  /** Filters currently differ from the contact-derived defaults ⇒ offer the reset. */
  canResetToContact: boolean;
  onResetToContact: () => void;
  queryText: string;
  onQueryTextChange: (query: string) => void;
  profileDiverges: boolean;
  suggestionDismissed: boolean;
  onAcceptProfileUpdate: () => Promise<void>;
  onDismissProfileSuggestion: () => void;
  isPaidTier: boolean;
};

function DiscoveryCard({
  scored,
  inMinisite,
  busy,
  marketCard,
  onOpen,
  onToggle
}: {
  scored: ScoredMatchHit;
  inMinisite: boolean;
  busy: boolean;
  marketCard: boolean;
  onOpen: (hit: MatchHit) => void;
  onToggle: (hit: MatchHit, inMinisite: boolean) => void;
}): JSX.Element {
  const theme = useTheme();
  const { userSettings } = useFirestore();
  const { hit, score } = scored;
  const addressLine = buildListingAddressLine(hit);
  // Clean (watermark-free) thumbs for any listing that has them, once the realtor has accepted the
  // rights terms — there is no per-listing gate any more (see utils/listings/watermarkFree.ts).
  // Search hits may not carry the watermark fields at all; then this degrades to the original cover.
  const thumbUrl = pickListingDisplayCoverUrl(
    userSettings?.watermarkTermsAcceptedAt ? hit : { ...hit, watermarkFreeImages: [] }
  );

  return (
    <Box
      onClick={() => onOpen(hit)}
      sx={{
        borderRadius: 1,
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer"
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 96,
          bgcolor: "grey.200",
          backgroundImage: thumbUrl
            ? `url(${thumbUrl})`
            : "linear-gradient(135deg, #dfe3e8 0%, #c4cdd5 100%)",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        {/* rendered first so the score/origin badges (later siblings) paint above it */}
        <SellerImageOverlay sellerName={hit.sellerName} agencyName={hit.agencyName} />
        {score !== null && (
          <Box sx={{ position: "absolute", top: 6, insetInlineStart: 6 }}>
            <Label color={score >= 80 ? "primary" : "warning"} variant="filled">
              {score}%
            </Label>
          </Box>
        )}
        {marketCard && ORIGIN_TAGS[hit.listingOrigin] && (
          <Box
            sx={{
              position: "absolute",
              bottom: 6,
              insetInlineStart: 6,
              px: 0.75,
              py: 0.1,
              borderRadius: 0.75,
              bgcolor: alpha(theme.palette.grey[900], 0.72),
              color: "common.white",
              typography: "caption"
            }}
          >
            {ORIGIN_TAGS[hit.listingOrigin]}
          </Box>
        )}
      </Box>

      <Stack spacing={0.25} sx={{ p: 1.25, flex: 1 }}>
        <Typography variant="subtitle2" noWrap title={addressLine}>
          {addressLine}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {buildListingMetaLine(hit)}
        </Typography>
        <Box>
          <ListingPriceWithHistory
            price={hit.price}
            priceHistory={hit.priceHistory}
            lastPriceChangeDate={hit.lastPriceChangeDate}
            variant="subtitle2"
          />
        </Box>
        {/* Org-mates' notes/meetings/tasks on this listing. */}
        <ListingOrgActivityIndicator listingId={hit.objectID} />
        <Box sx={{ pt: 0.5, mt: "auto" }}>
          {inMinisite ? (
            // Already added — the SAME button toggles it back out of the minisite.
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="success"
              disabled={busy}
              startIcon={busy ? <CircularProgress size={14} /> : <Check />}
              onClick={(e) => {
                e.stopPropagation();
                onToggle(hit, true);
              }}
              sx={{
                "&:hover": {
                  color: "error.main",
                  borderColor: "error.main",
                  bgcolor: alpha(theme.palette.error.main, 0.08)
                }
              }}
            >
              במיניסייט · להסרה
            </Button>
          ) : (
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="primary"
              disabled={busy}
              onClick={(e) => {
                e.stopPropagation();
                onToggle(hit, false);
              }}
              startIcon={busy ? <CircularProgress size={14} /> : undefined}
            >
              {marketCard ? "+ ייבא והוסף" : "+ הוסף למיניסייט"}
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

export default function AddListingsSubTab({
  contact,
  favorites,
  search,
  filters,
  onFiltersChange,
  filtersEdited,
  canResetToContact,
  onResetToContact,
  queryText,
  onQueryTextChange,
  profileDiverges,
  suggestionDismissed,
  onAcceptProfileUpdate,
  onDismissProfileSuggestion,
  isPaidTier
}: AddListingsSubTabProps): JSX.Element {
  const theme = useTheme();
  // The SURFACE, not the window — see `contactSurface.tsx`. The search field's own
  // `minWidth: 240` already claims most of a 420px side panel, so whether it is allowed to
  // sit BESIDE the filter controls has to be answered about this box.
  const isNarrow = useContactSurfaceDown("sm");
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const [quickViewListing, setQuickViewListing] = useState<MatchHit | null>(null);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  const firstName = getUserFirstNameFallBackToPhoneNumber(contact);
  const inMinisiteIds = new Set(favorites?.listingIds ?? []);

  const setBusy = (id: string, busy: boolean): void => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleToggle = async (hit: MatchHit, currentlyInMinisite: boolean): Promise<void> => {
    if (!user?.email) return;
    setBusy(hit.objectID, true);
    try {
      if (currentlyInMinisite) {
        await removeListingFromContactMinisite(contact.id, hit.objectID);
        enqueueSnackbar("הנכס הוסר מהמיניסייט", { variant: "success" });
      } else {
        await addListingToContactMinisite(user.email, contact.id, hit);
        enqueueSnackbar("הנכס נוסף למיניסייט", { variant: "success" });
      }
    } catch (err) {
      console.error("MatchesTab: failed to toggle listing in minisite", err);
      enqueueSnackbar(currentlyInMinisite ? "הסרת הנכס נכשלה, נסו שוב" : "הוספת הנכס נכשלה, נסו שוב", {
        variant: "error",
        autoHideDuration: 4000
      });
    } finally {
      setBusy(hit.objectID, false);
    }
  };

  // Skeletons until the FIRST pass completes (also while the user's orgs are still
  // loading, when the search effect can't run yet — a "no results" claim before any
  // search ran is a lie). A failed first pass shows a retry, not skeletons.
  //
  // This is deliberately NOT a terminal state: it renders forever if the effect never
  // fires, which is exactly how the Algolia→Postgres drift hid for a day (the null
  // index short-circuited the effect and the tab just span). Any new precondition
  // added to the search effect must fail into `error`, not into this.
  const showInitialSkeletons = !search.loadedOnce && !search.error;
  const showFirstError = Boolean(search.error) && !search.loadedOnce;

  // ── source counts → the tab strip ─────────────────────────────────────────
  // Extracted to plain numbers so the memo below has primitive deps (and so the
  // counts object stays identity-stable for the auto-select effect).
  const mineCount = search.segments.mine.length;
  const officeCount = search.segments.office.length;
  const marketCount = search.segments.market.length;
  const totalHits = mineCount + officeCount + marketCount;
  // Market is capped per pass, so its tab shows "N+" when the server reports more.
  const marketCountText =
    search.marketApproxTotal > marketCount ? `${marketCount}+` : `${marketCount}`;

  const sourceCounts = useMemo<Record<MatchSegmentId, AddListingsSourceCount>>(
    () => ({
      mine: { count: mineCount, text: `${mineCount}` },
      office: { count: officeCount, text: `${officeCount}` },
      market: { count: marketCount, text: marketCountText }
    }),
    [mineCount, officeCount, marketCount, marketCountText]
  );

  // `search.filtersKey` (filters + free-text query) identifies the result SET and
  // is written only on a successful pass — so the auto-select fires once per
  // result set and a manual tab choice survives every same-key refresh. See
  // useAddListingsSourceTab for the full policy.
  const { activeSource, selectSource } = useAddListingsSourceTab(
    sourceCounts,
    search.filtersKey ?? "",
    search.loadedOnce
  );
  const activeHits = search.segments[activeSource];
  const isMarketSource = activeSource === "market";
  // Non-paid users see the first FREE_MARKET_RESULTS market cards; the rest blur.
  const blurFrom = isMarketSource && !isPaidTier ? FREE_MARKET_RESULTS : Infinity;

  const quickViewInMinisite = Boolean(
    quickViewListing && inMinisiteIds.has(quickViewListing.objectID)
  );

  return (
    <Box sx={{ pt: 2 }}>
      {/* search bar (feeds the search) + filter chips */}
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
        <TextField
          size="small"
          value={queryText}
          onChange={(e) => onQueryTextChange(e.target.value)}
          placeholder="חיפוש חופשי — רחוב, שכונה, מילה מהתיאור..."
          sx={{ minWidth: 240, flex: isNarrow ? "1 1 100%" : "0 1 300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: "text.disabled" }} />
              </InputAdornment>
            )
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          ממוין לפי ציון התאמה
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {filtersEdited ? "סינון:" : "מסונן אוטומטית לפי פרופיל החיפוש:"}
        </Typography>
        <MatchFilterChips
          filters={filters}
          onChange={onFiltersChange}
          onOpenAdvanced={() => setFiltersDrawerOpen(true)}
          canResetToContact={canResetToContact}
          onResetToContact={onResetToContact}
        />
        {search.loading && search.loadedOnce && <CircularProgress size={14} />}
      </Stack>

      {/* The listings page's own filters panel, driven off this tab's filter state.
          Its "חיפוש" button is the single commit point — see MatchesFiltersDrawer. */}
      <MatchesFiltersDrawer
        open={filtersDrawerOpen}
        filters={filters}
        onClose={() => setFiltersDrawerOpen(false)}
        onApply={onFiltersChange}
      />

      {profileDiverges && !suggestionDismissed && (
        <Box sx={{ mt: 1.5 }}>
          <ProfileUpdateSuggestion
            contactFirstName={firstName}
            onAccept={onAcceptProfileUpdate}
            onDismiss={onDismissProfileSuggestion}
          />
        </Box>
      )}

      {showFirstError && (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="subtitle2" color="text.secondary">
            החיפוש נכשל
          </Typography>
          <Button size="small" startIcon={<Replay />} onClick={search.retry} sx={{ mt: 1 }}>
            נסו שוב
          </Button>
        </Box>
      )}

      {/* source strip — rendered during the initial load too (counts withheld
          until the first pass lands), so the panel doesn't jump when they arrive */}
      {!showFirstError && (
        <Box sx={{ mt: 2.5 }}>
          <AddListingsSourceTabs
            counts={sourceCounts}
            value={activeSource}
            onChange={selectSource}
            showCounts={search.loadedOnce}
          />
        </Box>
      )}

      {showInitialSkeletons && (
        <Box sx={{ mt: 2, ...cardsGridSx }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={210} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
      )}

      {/* the selected source's results */}
      {!showInitialSkeletons && !showFirstError && (
        <Box sx={{ mt: 2 }}>
          {activeHits.length > 0 && (
            <Box sx={cardsGridSx}>
              {activeHits.map((scored, index) => (
                <Box
                  key={scored.hit.objectID}
                  sx={
                    index >= blurFrom
                      ? { filter: "blur(7px)", pointerEvents: "none", userSelect: "none" }
                      : undefined
                  }
                >
                  <DiscoveryCard
                    scored={scored}
                    inMinisite={inMinisiteIds.has(scored.hit.objectID)}
                    busy={busyIds.has(scored.hit.objectID)}
                    marketCard={isMarketSource}
                    onOpen={setQuickViewListing}
                    onToggle={handleToggle}
                  />
                </Box>
              ))}
            </Box>
          )}

          {/* this source is empty but another one isn't — the counts on the strip
              say where to go, so this only has to name the gap */}
          {activeHits.length === 0 && totalHits > 0 && (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="subtitle2" color="text.secondary">
                {getAddListingsSource(activeSource).emptyText}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                יש תוצאות בלשוניות האחרות — בחרו לשונית למעלה
              </Typography>
            </Box>
          )}

          {isMarketSource && !isPaidTier && marketCount > FREE_MARKET_RESULTS && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1.5, color: theme.palette.warning.dark }}
            >
              תוצאות נוספות מהשוק זמינות במסלול בתשלום — שדרגו כדי לראות את כל הנכסים.
            </Typography>
          )}
        </Box>
      )}

      {!showInitialSkeletons && !showFirstError && !search.loading && totalHits === 0 && (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="subtitle2" color="text.secondary">
            לא נמצאו נכסים מתאימים
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {queryText.trim()
              ? "נסו לשנות את הטקסט בחיפוש או להרחיב את הסינון"
              : "נסו להרחיב את הסינון — טווח מחיר, אזורים או חדרים"}
          </Typography>
        </Box>
      )}

      {/* quick view — primary action mirrors the card's add/remove toggle */}
      <ListingQuickViewDialog
        open={Boolean(quickViewListing)}
        listing={quickViewListing}
        favorites={favorites}
        onClose={() => setQuickViewListing(null)}
        actions={
          quickViewListing && (
            <Button
              size="small"
              variant={quickViewInMinisite ? "outlined" : "contained"}
              color={quickViewInMinisite ? "success" : "primary"}
              disabled={busyIds.has(quickViewListing.objectID)}
              startIcon={quickViewInMinisite ? <Check /> : undefined}
              onClick={() => void handleToggle(quickViewListing, quickViewInMinisite)}
            >
              {quickViewInMinisite ? "במיניסייט · להסרה" : "+ הוסף למיניסייט"}
            </Button>
          )
        }
      />
    </Box>
  );
}

// Container-driven, not viewport-driven — see the fuller note on MinisiteSubTab's
// gridSx. Four columns in the ~812px this grid gets at a 900px window is ~194px a card,
// under the discovery card's own 224px min-content: the address truncated to a few
// characters and the add/remove button wrapped onto two lines. Measured card widths
// before — 161px @768, 194px @900, 199px @1200, 219px @1280 — i.e. cramped everywhere
// below a ~1366px window. After: 3 × 263–304px there, 4 columns again from 1366px up.
//
// The floor sits above that measured min-content so a track is never forced to grow;
// `& > *` minWidth:0 is the backstop. Discovery cards are lighter than minisite cards
// (no icon row), so they pack tighter.
const MIN_CARD_WIDTH_PX = 240;

const cardsGridSx = {
  display: "grid",
  gap: 1.5,
  gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${MIN_CARD_WIDTH_PX}px), 1fr))`,
  "& > *": { minWidth: 0 }
} as const;
